/**
 * PATH: packages/email-hub/src/feature/reader-header/lanes/ClassificationLane.tsx
 * FILE_ID: EMAILHUB-READERHEADER-LANES-CLASSIFICATION-001
 * PURPOSE: One category pill + cost-category marker + lock state for the email.
 * ROLE IN ARCHITECTURE: Bounded lane leaf of the expanded triage context.
 * MAIN EXPORTS: ClassificationLane.
 * NON-RESPONSIBILITIES: No picker implementation — CategoryChip owns it.
 * NOTES FOR FUTURE AI: The "Categorise" affordance is the SAME CategoryChip
 *   with an unset label, not a second control. Lock state renders as the
 *   chip's own lock glyph (category_locked) — never a parallel badge.
 *   Bill/supplier mail also offers "Structure" when the host wired
 *   EmailStatementViewer (change #432) — INTEL invoice chip is the primary
 *   entry; this covers bills that have no invoice# signal yet.
 */
import { FileText } from 'lucide-react'

import { CategoryChip } from '../../components/CategoryChip'
import CostCategoryChip, { shouldShowCostCategory } from '../../components/CostCategoryChip'
import {
  isStructuredDocCategory,
  useEmailStatementViewer,
} from '../../components/StatementViewer'
import { getCategoryConfig } from '../../lib/categoryConfig'
import { useOrgCustomCategories, usePersonalCategories } from '../../hooks/useEmailLabelPrefs'
import type { Email } from '../../hooks/useEmails'
import { cn } from '../../../_internal/lib/utils'
import { LANE_CONTROL_CLASS, LANE_CONTROL_ICON } from './laneControl'
import { LaneShell, type LaneShellProps } from './LaneShell'

export interface ClassificationLaneProps
  extends Pick<LaneShellProps, 'surfaceProps' | 'onOpenEllipsis' | 'expand'> {
  email: Email
  onRefetch?: () => void
}

/**
 * Renders the Classification lane.
 *
 * Inputs: email + refetch callback (CategoryChip mutates then refetches).
 * Output: the live CategoryChip (left-click opens its picker) plus the
 * cost-category marker when the config gates it on.
 * Failure mode: unset category shows the "Categorise" affordance on the
 * same chip — no dead empty lane.
 */
export function ClassificationLane({
  email,
  surfaceProps,
  onOpenEllipsis,
  onRefetch,
  expand,
}: ClassificationLaneProps) {
  const personalCategories = usePersonalCategories()
  const orgCustomCategories = useOrgCustomCategories()
  const customCategories = [...personalCategories, ...orgCustomCategories]
  const catCfg = getCategoryConfig(email.category || 'general', customCategories)
  const hasCategory = Boolean(email.category)
  const { openStructuredStatement } = useEmailStatementViewer()
  const showStructure =
    !!openStructuredStatement && isStructuredDocCategory(email.category)

  return (
    <LaneShell
      lane="classification"
      surfaceProps={surfaceProps}
      onOpenEllipsis={onOpenEllipsis}
      expand={expand}
    >
      <span data-reader-chip="category" className="inline-flex min-w-0">
        <CategoryChip
          emailId={email.id}
          category={email.category}
          categoryLocked={email.category_locked}
          pillClass={catCfg.pill}
          icon={catCfg.icon}
          label={hasCategory ? catCfg.label : 'Categorise'}
          size="sm"
          dotColor={catCfg.hex}
          onChanged={() => onRefetch?.()}
        />
      </span>
      {shouldShowCostCategory(email) && <CostCategoryChip email={email} />}
      {showStructure && (
        <button
          type="button"
          data-testid="classification-bill-structure"
          onClick={() => openStructuredStatement?.(String(email.id))}
          className={cn(
            LANE_CONTROL_CLASS,
            'border border-orange-500/40 bg-orange-500/10 font-semibold text-orange-800 hover:bg-orange-500/20 dark:text-orange-200',
          )}
          title="Open bill structure (line items and totals)"
        >
          <FileText className={LANE_CONTROL_ICON} aria-hidden />
          Structure
        </button>
      )}
    </LaneShell>
  )
}
